/** Courbe(s) sur axe temporel — la forme de base des pages Vue d'ensemble et Utilisateurs. */

import {
  ChartFrame,
  DEFAULT_PAD,
  Legend,
  useMeasuredWidth,
  alignSeries,
  buildPath,
  describeSeries,
  formatAxisValue,
  niceTicks,
  seriesMax,
  useTimeHover,
} from './chartUtils';
import type { Series } from './chartUtils';
import { formatDateTime, formatInt } from '../lib/format';

export interface LineChartProps {
  title: string;
  series: Series[];
  height?: number;
  /** Formatage de la valeur dans l'infobulle (par défaut : entier français). */
  formatValue?: (v: number) => string;
  formatX?: (t: string) => string;
  emptyLabel?: string;
  /**
   * Repères horizontaux en pointillés. Sert à poser sur une série journalière
   * une valeur qui n'EST pas une série — typiquement WAU/MAU, qui sont des
   * fenêtres glissantes d'utilisateurs uniques à l'instant T.
   */
  refLines?: { label: string; value: number; color: string }[];
}

export function LineChart(props: LineChartProps): React.ReactElement {
  const {
    title,
    series,
    height = 230,
    formatValue = formatInt,
    formatX = formatDateTime,
    emptyLabel,
    refLines = [],
  } = props;

  const [frameRef, vbWidth] = useMeasuredWidth<HTMLElement>();
  const { ts, values } = alignSeries(series);
  const pad = DEFAULT_PAD;
  const plotW = vbWidth - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const { hover, onPointerMove, onPointerLeave } = useTimeHover(ts.length, vbWidth, pad);

  // Les repères entrent dans le calcul de l'échelle : un repère hors cadre
  // donnerait l'illusion que la série le dépasse.
  const max = Math.max(seriesMax(values), ...refLines.map((r) => r.value), 0);
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1] ?? 1;
  const xOf = (i: number): number => (ts.length > 1 ? pad.left + (i / (ts.length - 1)) * plotW : pad.left + plotW / 2);
  const yOf = (v: number): number => pad.top + plotH - (v / top) * plotH;

  // Un axe des abscisses saturé d'étiquettes devient une bouillie : on n'en
  // garde qu'une poignée, régulièrement espacée.
  const labelEvery = Math.max(1, Math.ceil(ts.length / Math.max(2, Math.floor(plotW / 90))));

  const hoverT = hover ? ts[hover.index] : undefined;

  return (
    <ChartFrame
      ariaLabel={describeSeries(title, series)}
      empty={ts.length === 0 || max === 0}
      emptyLabel={emptyLabel}
      legend={series.length > 1 ? <Legend items={series} /> : undefined}
      frameRef={frameRef}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      tooltip={
        hover && hoverT !== undefined ? (
          <div className="chart-tip" style={{ left: `${hover.leftPct}%`, top: 0 }}>
            <span className="t-when">{formatX(hoverT)}</span>
            {series.map((s, si) => {
              const v = values[si]?.[hover.index];
              return (
                <span className="t-row" key={s.key}>
                  <i className="t-swatch" style={{ background: s.color }} />
                  {series.length > 1 ? `${s.label} ` : ''}
                  {v === null || v === undefined ? 'n/d' : formatValue(v)}
                </span>
              );
            })}
          </div>
        ) : undefined
      }
    >
      <svg viewBox={`0 0 ${vbWidth} ${height}`} role="img" aria-label={describeSeries(title, series)}>
        {/* Grille horizontale + graduations */}
        {ticks.map((tick) => (
          <g key={tick}>
            <line className="grid-line" x1={pad.left} x2={vbWidth - pad.right} y1={yOf(tick)} y2={yOf(tick)} />
            <text className="axis-label" x={pad.left - 8} y={yOf(tick) + 3.5} textAnchor="end">
              {formatAxisValue(tick)}
            </text>
          </g>
        ))}

        {/* Étiquettes de temps */}
        {ts.map((t, i) =>
          i % labelEvery === 0 ? (
            <text
              key={t}
              className="axis-label"
              x={xOf(i)}
              y={height - 8}
              textAnchor={i === 0 ? 'start' : i === ts.length - 1 ? 'end' : 'middle'}
            >
              {formatX(t)}
            </text>
          ) : null,
        )}

        {/* Repères de valeur (WAU / MAU) */}
        {refLines.map((ref) => (
          <g key={ref.label}>
            <line
              x1={pad.left}
              x2={vbWidth - pad.right}
              y1={yOf(ref.value)}
              y2={yOf(ref.value)}
              stroke={ref.color}
              strokeWidth={1.4}
              strokeDasharray="6 4"
              opacity={0.9}
            />
            <text
              className="axis-label"
              x={vbWidth - pad.right}
              y={yOf(ref.value) - 5}
              textAnchor="end"
              style={{ fill: ref.color }}
            >
              {ref.label} {formatAxisValue(ref.value)}
            </text>
          </g>
        ))}

        {/* Repère de survol, sous les courbes pour ne pas les masquer */}
        {hover ? (
          <line
            x1={xOf(hover.index)}
            x2={xOf(hover.index)}
            y1={pad.top}
            y2={pad.top + plotH}
            stroke="var(--c-ink3)"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        ) : null}

        {series.map((s, si) => {
          const row = values[si] ?? [];
          return (
            <g key={s.key}>
              <path
                d={buildPath(row, xOf, yOf)}
                fill="none"
                stroke={s.color}
                strokeWidth={2.2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {/*
                Points ISOLÉS — un point dont les deux voisins manquent ne produit
                aucun segment : sans ce marqueur, une série discontinue (ou deux
                séries dont les seaux ne coïncident pas exactement) donnerait un
                graphique vide alors que les valeurs sont là.
              */}
              {row.map((v, i) =>
                v !== null && (row[i - 1] ?? null) === null && (row[i + 1] ?? null) === null ? (
                  <circle key={i} cx={xOf(i)} cy={yOf(v)} r={2.6} fill={s.color} />
                ) : null,
              )}
              {hover
                ? (() => {
                    const v = row[hover.index];
                    return v === null || v === undefined ? null : (
                      <circle
                        cx={xOf(hover.index)}
                        cy={yOf(v)}
                        r={4}
                        fill="var(--c-surface)"
                        stroke={s.color}
                        strokeWidth={2.4}
                      />
                    );
                  })()
                : null}
            </g>
          );
        })}
      </svg>
    </ChartFrame>
  );
}
